import { css, Global } from '@emotion/core';
import { GLOBAL_CONFIG, useGetCustomStyles, useGetPageExtensionsMap, useGetPortalSummary } from 'api/hooks';
import { ChangePassword } from 'Components/ChangePassword';
import { FourOhFour } from 'Components/FourOhFour';
import React from 'react';
import { Helmet } from 'react-helmet';
import { Route, Switch, useHistory } from 'react-router-dom';
import { SWRConfig } from 'swr';
import { ErrorBoundary } from './Components/ErrorBoundary';
import { Login } from './Components/Login';
import { MainContent } from './Components/MainContent';
import './index.css';
import { defaultGlobalStyles } from './styles/globalStyles';
import './tailwind.generated.css';

function App() {
  const routerHistory = useHistory();
  const { customStyles } = useGetCustomStyles();
  const { portalSummary, portalSummaryError } = useGetPortalSummary();
  const { pageExtensionsMap } = useGetPageExtensionsMap();

  React.useEffect(() => {
    const goTo404 = () => {
      routerHistory.push({ pathname: '/404/' });
    };

    if (!!portalSummaryError && process.env.NODE_ENV === 'production') {
      goTo404();
    }
  }, [portalSummaryError]);

  React.useEffect(() => {
    // we use the DOMParser API to parse the strings into renderable html
    pageExtensionsMap?.forEach(([key, val]) => {
      const frag = document.createRange().createContextualFragment(val);

      document.getElementsByTagName('head')[0].appendChild(frag);
    });
  }, [pageExtensionsMap?.length]);

  React.useEffect(() => {
    document.title = portalSummary?.displayName ?? 'Portal';
  }, [portalSummary?.displayName]);

  return (
    <SWRConfig value={GLOBAL_CONFIG}>
      <Helmet>
        {customStyles?.faviconUrl && <link rel='shortcut icon' href={customStyles.faviconUrl} type='image/x-icon' />}
        {customStyles?.customCss && <style type='text/css'>{customStyles.customCss}</style>}
      </Helmet>
      <Global
        styles={{
          ...defaultGlobalStyles,
          ...(!!customStyles
            ? css`
                a {
                  ${!!customStyles?.customStyling?.secondaryColor &&
                  `color: ${customStyles?.customStyling?.secondaryColor};`}
                }

                #root {
                  ${!!customStyles?.customStyling?.backgroundColor &&
                  `background: ${customStyles?.customStyling?.backgroundColor};`}

                  ${!!customStyles?.customStyling?.defaultTextColor &&
                  `color: ${customStyles?.customStyling?.defaultTextColor};`}
                }
              `
            : {}),
          ...defaultGlobalStyles
        }}
      />

      <>
        <Switch>
          <Route path='/404/'>
            <FourOhFour />
          </Route>
          <Route path='/login/' exact>
            <ErrorBoundary fallback={<div>There was an error trying to get to the login page.</div>}>
              <Login />
            </ErrorBoundary>
          </Route>
          <Route path='/reset-password/' exact>
            <ErrorBoundary fallback={<div>There was an error trying to get to the password reset page.</div>}>
              <ChangePassword />
            </ErrorBoundary>
          </Route>
          <Route path='/'>
            <ErrorBoundary fallback={<div>There was a core error.</div>}>
              <MainContent />
            </ErrorBoundary>
          </Route>
        </Switch>
      </>
    </SWRConfig>
  );
}

export default App;
